import { Mail, Phone, MoreVertical, Star, Trash2 } from "lucide-react"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip"
import { cn } from "../../lib/utils"
import { getTagChipStyles } from "../../utils/tagHelpers"
import { useTheme } from "../../contexts/ThemeContext"

// Avatar gradient presets
const AVATAR_GRADIENTS = [
  "from-[#9f8e2c] to-[#d9cb2d]", // giallo/oro
  "from-[#994d97] to-[#a738ff]", // viola/magenta
  "from-[#994d97] to-[#e8d4fd]", // viola/lilla chiaro
  "from-[#837fe0] to-[#a738ff]", // blu/viola
  "from-[#068768] to-[#05df72]", // verde/teal
  "from-[#0684a9] to-[#51a2ff]", // azzurro/ciano
]

// Function to get deterministic gradient based on name
const getAvatarGradient = (name: string): string => {
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length]
}

export interface ContactRowProps {
  id: string
  name: string
  role?: string | null
  company_reference?: string | null
  company_name?: string | null
  avatar_url?: string | null
  tags: {id: number, name: string, color: string}[]
  hasPhone: boolean
  hasEmail: boolean
  mobile_phone?: string | null
  email?: string | null
  status: "active" | "inactive"
  selected?: boolean
  onClick?: () => void
  isFavorite?: boolean
  onToggleFavorite?: (contactId: string) => void
  onDelete?: () => void
}

export function ContactRow({
  id,
  name,
  role,
  company_reference,
  company_name,
  avatar_url,
  tags,
  hasPhone,
  hasEmail,
  mobile_phone,
  email,
  status,
  selected = false,
  onClick,
  isFavorite = false,
  onToggleFavorite,
  onDelete,
}: ContactRowProps) {
  const { theme } = useTheme()
  // Compute display values - only show if they have real values
  const displayRole = role?.trim() || null
  const displayCompany = (company_reference?.trim() || company_name?.trim()) || null

  // Build subtitle: "role · company", "role", "company", or nothing
  const subtitleParts = [displayRole, displayCompany].filter(Boolean)
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(" · ") : null

  // Generate initials from name
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const gradientClass = getAvatarGradient(name)

  return (
    <TooltipProvider>
      <div
        className={cn(
          "contact-row group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 px-4 py-3 md:py-1 min-h-[44px] transition-all cursor-pointer",
          "border-b border-[var(--hexa-border-subtle)] last:border-b-0",
          "hover:bg-[var(--hexa-border-subtle)]",
          selected && "bg-[var(--hexa-border)] hover:bg-[var(--hexa-border)]"
        )}
        onClick={onClick}
      >
        {/* Left column: Contact identity */}
        <div className="contact-row-identity flex items-center gap-3 min-w-0">
          {/* Avatar with image or gradient fallback */}
          <div className="flex-shrink-0">
            <Avatar className={cn("h-7 w-7 text-[var(--hexa-text-primary)] font-semibold text-[10px] bg-gradient-to-br", gradientClass)}>
              {avatar_url && (
                <AvatarImage
                  src={avatar_url}
                  alt={name}
                  className="object-cover"
                />
              )}
              <AvatarFallback className={cn("bg-gradient-to-br text-[var(--hexa-text-primary)] font-semibold text-[10px]", gradientClass)}>
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Text content */}
          <div className="flex-1 min-w-0">
            {/* Line 1: Name */}
            <h3 title={name} className="font-semibold text-[var(--hexa-text-primary)] text-sm md:text-xs break-words md:truncate mb-0.5">{name}</h3>
            {/* Line 2: Role • Company (only if at least one has value) */}
            {subtitle && (
              <p title={subtitle} className="text-xs md:text-[10px] text-[var(--hexa-text-secondary)] break-words md:truncate">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right column: Tags (always aligned right) */}
        <div className="contact-row-meta flex min-w-0 items-center gap-2">
          {/* Favorite star button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  "contact-favorite h-10 w-10 md:h-7 md:w-7",
                  isFavorite ? "text-yellow-400 hover:text-yellow-300" : "text-white/40 hover:text-white/60"
                )}
                aria-label={isFavorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                aria-pressed={isFavorite}
                onClick={(e) => {
                  e.stopPropagation()
                  if (onToggleFavorite) {
                    onToggleFavorite(id)
                  }
                }}
              >
                <Star className={cn("w-3.5 h-3.5", isFavorite && "fill-yellow-400")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isFavorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
            </TooltipContent>
          </Tooltip>

          {/* Tags section */}
          {tags.length > 0 && (
            <div className="contact-row-tags flex min-w-0 flex-wrap gap-1">
              {tags.slice(0, 2).map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex min-w-0 max-w-full md:max-w-[120px] items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border"
                  title={tag.name}
                  style={getTagChipStyles(tag.color, theme)}
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                  <span className="min-w-0 break-words md:truncate">{tag.name}</span>
                </span>
              ))}
              {tags.length > 2 && (
                <Badge
                  variant="secondary"
                  className="bg-[var(--hexa-border-subtle)] text-[var(--hexa-text-primary)] text-[10px] border-[var(--hexa-border)] px-2 py-0.5 h-5"
                >
                  +{tags.length - 2}
                </Badge>
              )}
            </div>
          )}

          {/* Action buttons - hidden by default, visible on hover */}
          <div className="contact-row-actions flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={!hasPhone || !mobile_phone}
                  aria-label="Chiama contatto"
                  className={cn(
                    "h-10 w-10 md:h-7 md:w-7",
                    hasPhone
                      ? "text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
                      : "text-[var(--hexa-text-muted)] cursor-not-allowed"
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (mobile_phone) {
                      window.location.href = `tel:${mobile_phone.replace(/[^\d+*#;,]/g, "")}`
                    }
                  }}
                >
                  <Phone className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {hasPhone ? "Chiama contatto" : "Numero non disponibile"}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={!hasEmail || !email}
                  aria-label="Invia email"
                  className={cn(
                    "h-10 w-10 md:h-7 md:w-7",
                    hasEmail
                      ? "text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
                      : "text-[var(--hexa-text-muted)] cursor-not-allowed"
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (email) {
                      window.location.href = `mailto:${encodeURIComponent(email)}`
                    }
                  }}
                >
                  <Mail className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {hasEmail ? "Invia email" : "Email non disponibile"}
              </TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Altre opzioni per ${name}`}
                  className="h-10 w-10 md:h-7 md:w-7 text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <MoreVertical className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                <DropdownMenuItem className="min-h-10" onSelect={() => onClick?.()}>
                  Dettagli contatto
                </DropdownMenuItem>
                {onDelete && <DropdownMenuItem className="min-h-10" variant="destructive" onSelect={onDelete}>
                  <Trash2 /> Elimina contatto
                </DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
